import { useState, useEffect, useRef } from 'react';
import mixpanel from 'mixpanel-browser';
import { useFormValidation } from './useFormValidation';
import { findInventorySource, extractSessionId } from '../utils/shopUtils';
import { SESSION_STORAGE_KEYS, CONNECTION_KIND, CONNECTION_TYPE, OUTLET_MAPPING_STORAGE_PREFIX } from '../constants';
import { OAUTH_POLLING_EVENT } from './useOAuthCallback';
import { initiateConnection } from '../services/connectionFlows';

/**
 * Manages connection state for stock or product inventory sources
 *
 * @param {'stock'|'product'} kind - Connection kind
 * @param {Object} currentShop - Current shop object
 * @param {Array} posList - List of available POS providers
 * @returns {Object} Connection state and handlers
 */
export const useConnectionState = (kind, currentShop, posList) => {
	const [editing, setEditing] = useState(false);
	const [editingPOS, setEditingPOS] = useState('');
	const [formData, setFormData] = useState({});
	const [sessionId, setSessionId] = useState(() => {
		const key = kind === CONNECTION_KIND.STOCK ? SESSION_STORAGE_KEYS.STOCK_SESSION : SESSION_STORAGE_KEYS.PRODUCT_SESSION;
		return sessionStorage.getItem(key) || null;
	});
	const [oauthParams, setOauthParams] = useState({});
	const [pollingForConnection, setPollingForConnection] = useState(false);
	const [connectionWarning, setConnectionWarning] = useState(null);
	// Multi-outlet POSes return an outlet list from the OAuth exchange; ingest is
	// withheld until an outlet is picked (NEA-5988). {sessionId, shopId, locations}.
	const [pendingOutletMapping, setPendingOutletMapping] = useState(null);

	const source = findInventorySource(currentShop, kind);
	const selectedPOS = editing || !source?.pointOfSale ? editingPOS : source?.pointOfSale || '';
	const provider = selectedPOS && posList.length > 0 ? posList.find((p) => p.value === selectedPOS) : null;

	const formErrors = useFormValidation(provider, formData, editing);

	useEffect(() => {
		const handleOAuthComplete = (event) => {
			if (event.detail.connectionKind !== kind) return;
			if (!event.detail.success) return;

			const { locations, sessionId: exchangeSessionId, shopId } = event.detail;
			if (locations?.length && exchangeSessionId && shopId) {
				// Persist under the SESSION's shop id (never currentShop): stepper
				// clicks unmount this provider, and the OAuth code was already
				// consumed — without persistence the only recovery is a re-OAuth.
				const mapping = { sessionId: exchangeSessionId, shopId, locations };
				sessionStorage.setItem(OUTLET_MAPPING_STORAGE_PREFIX + shopId, JSON.stringify(mapping));
				// Only surface the picker when this page is actually showing that
				// shop — a chain tab visiting a sibling's onboarding must not be
				// visually hijacked by another shop's outlet list.
				if (!currentShop?.id || shopId === currentShop.id) {
					setPendingOutletMapping(mapping);
				}
				return;
			}

			setPollingForConnection(true);
		};

		window.addEventListener(OAUTH_POLLING_EVENT, handleOAuthComplete);
		return () => window.removeEventListener(OAUTH_POLLING_EVENT, handleOAuthComplete);
	}, [kind, currentShop?.id]);

	// Rehydrate a pending outlet mapping (stepper clicks / reloads survive), but
	// never over a source that already has an outlet — a mapping completed via
	// another path makes the stored one stale.
	useEffect(() => {
		if (kind !== CONNECTION_KIND.STOCK || !currentShop?.id || pendingOutletMapping) return;
		const storageKey = OUTLET_MAPPING_STORAGE_PREFIX + currentShop.id;
		const stored = sessionStorage.getItem(storageKey);
		if (!stored) return;
		if (source?.locationId) {
			sessionStorage.removeItem(storageKey);
			return;
		}
		try {
			const parsed = JSON.parse(stored);
			if (parsed?.sessionId && parsed?.shopId && parsed?.locations?.length) {
				setPendingOutletMapping(parsed);
			}
		} catch {
			sessionStorage.removeItem(storageKey);
		}
	}, [kind, currentShop?.id, source?.locationId, pendingOutletMapping]);

	const clearPendingOutletMapping = () => {
		if (pendingOutletMapping?.shopId) {
			sessionStorage.removeItem(OUTLET_MAPPING_STORAGE_PREFIX + pendingOutletMapping.shopId);
		}
		setPendingOutletMapping(null);
	};

	// Same persistence + shop-scoping semantics as the OAuth event path, for
	// mappings sourced elsewhere (the outlet fast path).
	const adoptOutletMapping = (mapping) => {
		if (!mapping?.sessionId || !mapping?.shopId || !mapping?.locations?.length) return;
		sessionStorage.setItem(OUTLET_MAPPING_STORAGE_PREFIX + mapping.shopId, JSON.stringify(mapping));
		if (!currentShop?.id || mapping.shopId === currentShop.id) {
			setPendingOutletMapping(mapping);
		}
	};

	useEffect(() => {
		if (editing && provider?.connect?.steps) {
			const defaults = {};
			for (const step of provider.connect.steps) {
				if (step.fields) {
					for (const field of step.fields) {
						if (field.saveAs && field.value) {
							defaults[field.saveAs] = field.value;
						}
					}
				}
			}
			setFormData(defaults);
		}
	}, [editing, provider]);

	// Auto-create session when editing a plug-and-play connection without a sessionId
	// This handles two scenarios:
	// 1. Editing an existing connection that was set up in a different session or after sessionStorage was cleared
	// 2. Switching from one POS provider to another whilst in edit mode
	const sessionInitiatedRef = useRef(false);
	const previousSelectedPOS = useRef(selectedPOS);

	// Clear session when POS changes whilst editing
	useEffect(() => {
		// When the selected POS changes whilst editing, clear the old session and reset the ref
		// This ensures a fresh session is created for the new POS provider
		if (editing && selectedPOS && previousSelectedPOS.current !== selectedPOS) {
			previousSelectedPOS.current = selectedPOS;
			sessionInitiatedRef.current = false;
			// Clear any old session since it's for a different POS
			setSessionId(null);
			const key = kind === CONNECTION_KIND.STOCK ? SESSION_STORAGE_KEYS.STOCK_SESSION : SESSION_STORAGE_KEYS.PRODUCT_SESSION;
			sessionStorage.removeItem(key);
			// Also clear polling state when switching providers
			setPollingForConnection(false);
			setConnectionWarning(null);
		}

		// Reset refs when exiting edit mode to allow future edits
		if (!editing) {
			sessionInitiatedRef.current = false;
			previousSelectedPOS.current = '';
		}
	}, [editing, selectedPOS, kind]);

	// Create session for plug-and-play connections when editing without a sessionId
	useEffect(() => {
		const shouldInitiateSession =
			editing && !sessionId && provider?.connection === CONNECTION_TYPE.PLUG_PLAY && !sessionInitiatedRef.current && selectedPOS; // Only initiate if a POS is actually selected

		if (shouldInitiateSession && currentShop?.id) {
			sessionInitiatedRef.current = true;

			const createSession = async () => {
				try {
					const redirectUrl = `${window.location.origin}${window.location.pathname}`;
					const response = await initiateConnection({
						shopId: currentShop.id,
						provider: selectedPOS,
						kind,
						redirectUrl
					});

					const newSessionId = extractSessionId(response);
					if (newSessionId) {
						updateSessionId(newSessionId);
					}
				} catch (error) {
					console.error('Failed to auto-create session when editing connection:', error);
					// Don't block the UI - user can still attempt to reconnect manually
				}
			};

			createSession();
		}
	}, [editing, sessionId, provider, currentShop?.id, selectedPOS, kind]);

	const handleFormChange = (fieldPath, value) => {
		setFormData((prev) => ({ ...prev, [fieldPath]: value }));
		setConnectionWarning(null);
	};

	const handleEdit = () => {
		setEditing(true);
		setEditingPOS(source?.pointOfSale || '');
		setConnectionWarning(null);
	};

	const handleCloseEdit = () => {
		setEditing(false);
		setEditingPOS('');
		setConnectionWarning(null);
	};

	// Track POS selection with Mixpanel
	const trackablePOSChange = (newPOS) => {
		if (newPOS && currentShop?.id && posList.length > 0) {
			const selectedProvider = posList.find((p) => p.value === newPOS);
			if (selectedProvider) {
				mixpanel.track('Unify POS selected', {
					distinct_id: currentShop.id,
					provider: selectedProvider.value,
					connection_type: selectedProvider.connection
				});
			}
		}
		setEditingPOS(newPOS);
	};

	const updateSessionId = (newSessionId) => {
		setSessionId(newSessionId);
		const key = kind === CONNECTION_KIND.STOCK ? SESSION_STORAGE_KEYS.STOCK_SESSION : SESSION_STORAGE_KEYS.PRODUCT_SESSION;
		sessionStorage.setItem(key, newSessionId);
	};

	return {
		editing,
		formData,
		formErrors,
		oauthParams,
		sessionId,
		pollingForConnection,
		connectionWarning,
		pendingOutletMapping,
		clearPendingOutletMapping,
		adoptOutletMapping,
		source,
		selectedPOS,
		provider,
		setEditing,
		setEditingPOS: trackablePOSChange,
		setFormData,
		setOauthParams,
		setPollingForConnection,
		setConnectionWarning,
		handleFormChange,
		updateSessionId
	};
};
